import { MCP_PATH, type McpConnection } from '@ftthnms/shared';
import { Button, EmptyState } from '@ftthnms/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Check, Copy, Unplug } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RequireAbility, Tabs } from '../components/common';
import { useToast } from '../components/Toast';
import { api, dateTime } from '../lib/api';
import { useSession } from '../lib/session';

/** AI agents over MCP: how to connect, and the connections that can be revoked. */
export function AiAgentPage() {
  return (
    <RequireAbility action="use" subject="Mcp">
      <AiAgent />
    </RequireAbility>
  );
}

function AiAgent() {
  const { ability } = useSession();
  const isAdmin = ability.can('manage', 'User');
  const [tab, setTab] = useState<'mine' | 'all'>('mine');
  return (
    <div className="fn-page-body">
      <ConnectGuide />
      {isAdmin && (
        <Tabs
          tabs={[
            ['mine', 'Koneksi saya'],
            ['all', 'Semua pengguna'],
          ]}
          value={tab}
          onChange={setTab}
        />
      )}
      <Connections all={isAdmin && tab === 'all'} />
    </div>
  );
}

function ConnectGuide() {
  const url = `${window.location.origin}${MCP_PATH}`;
  return (
    <section className="fn-card fn-card--pad flex flex-col gap-4">
      <div>
        <h3 className="fn-card__title">Hubungkan AI agent</h3>
        <p className="fn-card__desc">
          AI agent (Claude, Cursor, dan klien MCP lain) bisa mencari data, menelusuri redaman, membuat BOQ,
          serta menambah atau mengubah aset dan jalur atas nama Anda, sebatas hak akses peran Anda. Saat
          pertama tersambung, browser membuka login Speedmax ID lalu halaman persetujuan.
        </p>
      </div>
      <CopyField label="URL server MCP" value={url} />
      <div className="grid gap-4 md:grid-cols-3">
        <Step title="Claude (web / desktop)">
          Settings → Connectors → <i>Add custom connector</i>, tempel URL di atas, lalu <i>Connect</i>.
        </Step>
        <Step title="Claude Code">
          <CopyField value={`claude mcp add --transport http ftthnms ${url}`} compact />
          Lalu jalankan <span className="mono">/mcp</span> dan pilih ftthnms untuk login.
        </Step>
        <Step title="Cursor / VS Code">
          Tambahkan ke konfigurasi MCP:
          <CopyField value={`{"mcpServers":{"ftthnms":{"url":"${url}"}}}`} compact />
        </Step>
      </div>
      <p className="fn-hint">
        Contoh perintah: "ODP mana saja di Cipongkor yang sudah penuh?", "Tambah pelanggan Budi di -6.91,
        107.61 dan sambungkan ke ODP terdekat", "Trace redaman pelanggan CPE-CPK-012".
      </p>
    </section>
  );
}

function Step({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="fn-label">{title}</span>
      <div className="fn-card__desc flex flex-col gap-2">{children}</div>
    </div>
  );
}

function CopyField({ label, value, compact }: { label?: string; value: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      {label && <span className="fn-label">{label}</span>}
      <div className="flex gap-2">
        <input
          className="fn-input mono"
          readOnly
          value={value}
          aria-label={label ?? 'Perintah'}
          style={compact ? { fontSize: 12 } : undefined}
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button
          size="sm"
          variant="secondary"
          icon={copied ? <Check className="i" aria-hidden /> : <Copy className="i" aria-hidden />}
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? 'Tersalin' : 'Salin'}
        </Button>
      </div>
    </div>
  );
}

function Connections({ all }: { all: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState<McpConnection | null>(null);
  const list = useQuery({
    queryKey: ['mcp-connections', all],
    queryFn: () => api<McpConnection[]>(`/api/v1/mcp/connections${all ? '?all=1' : ''}`),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/v1/mcp/connections/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast({ tone: 'success', text: 'Akses AI agent dicabut.' });
      void qc.invalidateQueries({ queryKey: ['mcp-connections'] });
    },
    onError: (err) => toast({ tone: 'danger', text: err.message }),
  });

  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">{all ? 'Koneksi semua pengguna' : 'Koneksi saya'}</h3>
          <p className="fn-card__desc">
            Satu baris = satu AI agent yang Anda izinkan. Koneksi berakhir sendiri bila tidak dipakai 30 hari,
            dan ikut terputus saat Anda keluar dengan pilihan "Putuskan juga AI agent" atau keluar dari
            Speedmax ID.
          </p>
        </div>
      </div>
      {list.data && list.data.length === 0 ? (
        <EmptyState
          icon={<Bot className="i i--xl" aria-hidden />}
          title="Belum ada AI agent tersambung"
          text="Tambahkan URL server MCP di aplikasi AI agent Anda untuk mulai."
        />
      ) : (
        <div className="fn-table-wrap">
          <table className="fn-table">
            <thead>
              <tr>
                <th>AI agent</th>
                {all && <th>Pengguna</th>}
                <th>Kembali ke</th>
                <th>Diizinkan</th>
                <th>Terakhir dipakai</th>
                <th className="is-end">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.map((c) => (
                <tr key={c.id}>
                  <td>{c.clientName}</td>
                  {all && <td>{c.user ? `${c.user.name} (${c.user.username})` : '—'}</td>}
                  <td className="mono">{c.redirectHost}</td>
                  <td>{dateTime.format(new Date(c.createdAt))}</td>
                  <td>{c.lastUsedAt ? dateTime.format(new Date(c.lastUsedAt)) : '—'}</td>
                  <td className="is-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Unplug className="i" aria-hidden />}
                      onClick={() => setConfirm(c)}
                    >
                      Cabut
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={`Cabut akses ${confirm.clientName}?`}
          icon={<Unplug className="i" aria-hidden />}
          confirmLabel="Cabut akses"
          onConfirm={async () => {
            await revoke.mutateAsync(confirm.id);
            setConfirm(null);
          }}
          onClose={() => setConfirm(null)}
        >
          <p>
            Agent langsung tidak bisa memakai FTTH NMS lagi. Untuk memakai lagi, hubungkan ulang dan login.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
